import config from '@app/config'
import ws from '@api/ws'
import upload from './file_ws'

interface WSResponse {
  code: string
  message: string
}

const apis = {
  user: {
    login: () => ws<UserAccount>(`${config.API_VERSION}${config.ws.user.login}`, 'GET'),
    getCgus: () => ws<Cgu>(`${config.API_VERSION}${config.ws.user.cgus}`, 'GET'),
    acceptCgus: () => ws<UserAccount>(`${config.API_VERSION}${config.ws.user.cgus}`, 'POST'),
  },
  business: {
    get: (idBusiness: string) =>
      ws<BusinessDetails>(`${config.API_VERSION}${config.ws.business.get}${idBusiness}`, 'GET'),
    list: (filters: BusinessesFilterRequest) =>
      ws<BusinessResume[]>(`${config.API_VERSION}${config.ws.business.list}`, 'POST', filters),
    details: (idBusiness: string) =>
      ws<AllBusinessDetails>(`${config.API_VERSION}${config.ws.business.details}${idBusiness}`, 'GET'),
    comment: (idBusiness: string, comment: string) =>
      ws<BusinessDetailsComment>(`${config.API_VERSION}${config.ws.business.comment}${idBusiness}`, 'POST', {
        comment,
      }),
    map: (filters: BusinessesMapFilterRequest) =>
      ws<BusinessMap>(`${config.API_VERSION}${config.ws.business.map}`, 'POST', filters),
  },
  declaration: {
    edit: (declaration: Declaration) =>
      ws<Declaration>(`${config.API_VERSION}${config.ws.declaration.edit}${declaration.id}`, 'PUT', declaration),
    releaseBreakpoint: (idDeclaration: string) =>
      ws<void>(
        `${config.API_VERSION}${config.ws.declaration.releaseBreakpoint.replace('{idDeclaration}', idDeclaration)}`,
        'PUT'
      ),
    pictures: {
      getPicture: (pictureId: string) =>
        ws<string>(
          `${config.API_VERSION}${config.ws.declaration.pictures.getPicture.replace('{pictureId}', pictureId)}`,
          'GET',
          undefined,
          'arraybuffer'
        ),
    },
  },
  company: {
    list: () => ws<Company[]>(`${config.API_VERSION}${config.ws.company.list}`, 'GET'),
    listCompanyGroups: () =>
      ws<SimpleCompanyGroup[]>(`${config.API_VERSION}${config.ws.company.listCompanyGroups}`, 'GET'),
  },
  responsible: {
    list: () => ws<Responsible[]>(`${config.API_VERSION}${config.ws.responsible.list}`, 'GET'),
  },
  step: {
    list: () => ws<BusinessStep[]>(`${config.API_VERSION}${config.ws.step.list}`, 'GET'),
  },
  orphan: {
    list: (filters: OrphansListFilterRequest) =>
      ws<OrphanDeclarationResume[]>(`${config.API_VERSION}${config.ws.orphan.list}`, 'POST', filters),
    get: (idOrphan: string) =>
      ws<OrphanDeclarationDetails>(`${config.API_VERSION}${config.ws.orphan.get}${idOrphan}`, 'GET'),
    delete: (idOrphan: string) =>
      ws<WSResponse>(`${config.API_VERSION}${config.ws.orphan.delete}${idOrphan}`, 'DELETE'),
    affect: (idOrphan: string, idBusiness: string, idTheme: string) =>
      ws<Declaration>(`${config.API_VERSION}${config.ws.orphan.affect}${idOrphan}`, 'POST', {
        idBusiness,
        idTheme,
      }),
  },
  admin: {
    list: {
      regions: () => ws<AdminRegions>(`${config.API_VERSION}${config.ws.admin.list.regions}`, 'GET'),
      companies: () => ws<AdminCompanies>(`${config.API_VERSION}${config.ws.admin.list.companies}`, 'GET'),
      internals: () => ws<OptionUsers>(`${config.API_VERSION}${config.ws.admin.list.internals}`, 'GET'),
      externals: () => ws<OptionUsers>(`${config.API_VERSION}${config.ws.admin.list.externals}`, 'GET'),
      businesses: (filters: AdminBusinessFilter) =>
        ws<OptionBusinesses>(`${config.API_VERSION}${config.ws.admin.list.businesses}`, 'POST', filters),
    },
    internal: {
      list: () => ws<AdminInternals>(`${config.API_VERSION}${config.ws.admin.internal.list}`, 'GET'),
      edit: (user: AdminInternal) =>
        ws<AdminInternal>(`${config.API_VERSION}${config.ws.admin.internal.edit}${user.id}`, 'PUT', user),
      add: (user: AdminInternalCreate) =>
        ws<AdminInternal>(`${config.API_VERSION}${config.ws.admin.internal.add}`, 'POST', user),
      delete: (id: string) => ws(`${config.API_VERSION}${config.ws.admin.internal.delete}${id}`, 'DELETE'),
    },
    external: {
      list: () => ws<AdminExternals>(`${config.API_VERSION}${config.ws.admin.external.list}`, 'GET'),
    },
    company: {
      get: (id: string) => ws<AdminCompany>(`${config.API_VERSION}${config.ws.admin.company.get}${id}`, 'GET'),
      listBusinesses: (idCompany: string) =>
        ws<AdminCompanyBusinesses>(
          `${config.API_VERSION}${config.ws.admin.company.listBusinesses.replace('{idCompany}', idCompany)}`,
          'GET'
        ),
      getUsers: (idCompany: string, params?: string) =>
        ws<AdminCompanyUsers>(
          `${config.API_VERSION}${config.ws.admin.company.getUsers.replace('{idCompany}', idCompany)}?${params}`,
          'GET'
        ),
    },
    companyGroup: {
      get: () => ws<CompanyGroups>(`${config.API_VERSION}${config.ws.admin.companyGroup.get}`, 'GET'),
      add: (companyGroup: CompanyGroupInput) =>
        ws<CompanyGroup>(`${config.API_VERSION}${config.ws.admin.companyGroup.add}`, 'POST', companyGroup),
      edit: (id: string, companyGroup: CompanyGroupInput) =>
        ws<CompanyGroup>(`${config.API_VERSION}${config.ws.admin.companyGroup.edit}${id}`, 'PUT', companyGroup),
      delete: (id: string) => ws(`${config.API_VERSION}${config.ws.admin.companyGroup.delete}${id}`, 'DELETE'),
    },
    business: {
      list: (filters: AdminBusinessFilter) =>
        ws<AdminBusinesses>(`${config.API_VERSION}${config.ws.admin.business.list}`, 'POST', filters),
      edit: (business: AdminBusiness) =>
        ws<AdminBusiness>(`${config.API_VERSION}${config.ws.admin.business.edit}${business.id}`, 'PUT', business),
      board: (queryParams: string) =>
        ws<AdminBusinessesExtended>(`${config.API_VERSION}${config.ws.admin.business.board}?${queryParams}`, 'GET'),
      export: (queryParams: string) =>
        ws<string>(`${config.API_VERSION}${config.ws.admin.business.export}?${queryParams}`, 'GET'),
      import: (importFile: any) =>
        upload(`${config.API_VERSION}${config.ws.admin.business.import}`, 'POST', importFile),
    },
    export: {
      users: ({ type, daysSinceCreation }: AdminExportUsers) =>
        ws<string>(
          `${config.API_VERSION}${config.ws.admin.export.users}?type=${type}&daysSinceCreation=${daysSinceCreation}`,
          'GET'
        ),
      declarations: ({ type, searchId }: AdminExportDeclarations) =>
        ws<string>(
          `${config.API_VERSION}${config.ws.admin.export.declarations}?${
            type === 'user' ? `userId` : `companyId`
          }=${searchId}`,
          'GET'
        ),
      stats: ({ type, daysSinceCreation, regionId }: AdminStatisticsRequest) =>
        ws<BusinessStatsSimplified>(
          `${config.API_VERSION}${config.ws.admin.export.stats}?type=${type}&daysSinceCreation=${daysSinceCreation}&regionId=${regionId}`,
          'GET'
        ),
    },
    import: {
      internal: {
        add: (importFile: any) =>
          upload(`${config.API_VERSION}${config.ws.admin.import.internals.add}`, 'POST', importFile),
        edit: (importFile: any) =>
          upload(`${config.API_VERSION}${config.ws.admin.import.internals.edit}`, 'PATCH', importFile),
        delete: (importFile: any) =>
          upload(`${config.API_VERSION}${config.ws.admin.import.internals.delete}`, 'DELETE', importFile),
      },
      external: {
        add: (importFile: any) =>
          upload(`${config.API_VERSION}${config.ws.admin.import.externals.add}`, 'POST', importFile),
        edit: (importFile: any) =>
          upload(`${config.API_VERSION}${config.ws.admin.import.externals.edit}`, 'PATCH', importFile),
        delete: (importFile: any) =>
          upload(`${config.API_VERSION}${config.ws.admin.import.externals.delete}`, 'DELETE', importFile),
      },
    },
  },
}

export default apis
